<template>
	<div v-if="loaded">
		<header class="page-title">
			<h2>{{ providerType.translations['en'].name }} &ndash; Property Groups</h2>
			<div class="ml-auto" v-if="!userRoles.includes('users.translator')">
				<a @click.prevent="showCreateModal" class="btn btn-md btn-success" href="#"><i class="fas fa-plus"></i> New Property Group</a>
			</div>
		</header>

		<div class="card nav-horizontal-contents" v-for="(group, index) in providerType.groups">
			<header class="section-title">
				<h3>{{ group.name }}</h3>
				<div class="ml-auto">
					<a @click.prevent="showEditModal(index)" class="btn btn-md btn-primary"><i class="fas fa-pencil-alt"></i> Edit Property Group</a>
					<a :href="'/' + selectedSite.slug + '/provider-types/' + providerType.id + '/groups/' + group.id + '/property/create'" class="btn btn-md btn-success" v-show="!userRoles.includes('users.translator')" target="_blank"><i class="fas fa-plus"></i> New Property</a>
				</div>
			</header>
			<div class="table-responsive">
				<table>
					<thead>
						<tr>
							<th></th>
							<th>Name</th>
							<th>ID</th>
							<th>Type</th>
							<th>Status</th>
							<th>Actions</th>
						</tr>
					</thead>
					<draggable :tag="'tbody'" @change="arrangePropertyOrder(index)" @end="drag=false" @start="drag=true" v-model="group.properties">
						<tr class="draggable" v-for="(property, propertyIndex) in group.properties">

							<td class="text-center"><i class="fas fa-grip-lines"></i></td>
							<td>{{ property.name }}</td>
							<td>{{ property.id }}</td>
							<td>{{ property.type }}</td>
							<td>
								<span :class="property.hidden ? 'badge-default' : 'badge-primary'" class="badge">{{ property.hidden ? 'Hidden' : 'Visible' }}</span>
								<span class="badge-warning badge"
									  title="This property is visible on Provider Portal"
									  v-if="property.visible_on_provider_portal">PP</span>
							</td>
							<td class="nowrap">
								<a :href="'/' + selectedSite.slug + '/provider-types/' + providerType.id + '/groups/' + group.id + '/property/' + property.id" class="btn btn-icon btn-primary" title="Edit" target="_blank"><i class="fas fa-pencil-alt"></i></a>
								<a @click.prevent="deleteProperty(property.id)" class="btn btn-icon btn-danger" title="Delete" v-if="userRoles.includes('provider_property.group.delete') && !userRoles.includes('users.translator')"><i class="fas fa-trash-alt"></i></a>
							</td>
						</tr>
					</draggable>
				</table>
			</div>
		</div>
		<div class="modal-bg" v-if="groupModal">
			<div class="modal">
				<header class="modal-header">
					<h4 class="modal-title">{{ selectedGroup.id === null ? 'New Property Group' : 'Edit Property Group' }}</h4>
					<a @click.prevent="closeGroupModal" class="modal-close" title="Close modal"><i class="fas fa-times"></i></a>
				</header>
				<section class="modal-body">
					<div class="card">
						<header class="card-header language-list">
							<lang-selector></lang-selector>
						</header>
						<div class="card-body">
							<div class="form-group">
								<label for="name">Name</label>
								<input id="name" type="text" v-model="selectedGroup[clickedLang].name" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
								<p class="input-error" v-if="errors.errors[errorNameCurrentLanguage]">{{ errors.errors[errorNameCurrentLanguage] }}</p>
							</div>
							<div class="form-group">
								<label for="description">Description</label>
								<textarea class="textarea-small" id="description" v-model="selectedGroup[clickedLang].description" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale"></textarea>
								<p class="input-error" v-if="errors.errors[errorDescriptionCurrentLanguage]">{{ errors.errors[errorDescriptionCurrentLanguage] }}</p>
							</div>
						</div>
					</div>
					<div class="form-group">
						<label for="internal-description">Internal Description</label>
						<textarea class="textarea-small" id="internal-description" v-model="selectedGroup.internal_description"></textarea>
					</div>
					<div class="form-group" v-if="!userRoles.includes('users.translator')">
						<label class="label-checkbox">
							<input type="checkbox" v-model="selectedGroup.hidden">
							<span>Hidden Property Group</span>
						</label>
					</div>
				</section>
				<footer class="modal-footer center">
					<a @click.prevent="closeGroupModal" class="btn btn-md btn-primary modal-close" href="#" v-show="selectedGroup.id === null"><i class="fas fa-times"></i> Cancel</a>
					<a @click.prevent="createGroup" class="btn btn-md btn-success" href="#" v-show="selectedGroup.id === null"><i class="fas fa-plus"></i> New Property Group</a>

					<a @click.prevent="showDeleteConfirm" class="mr-auto text-danger" v-if="!deleteConfirm" v-show="selectedGroup.id !== null && userRoles.includes('provider_property.group.delete') && !userRoles.includes('users.translator')"><i class="fas fa-trash icon-left"></i>Delete Property Group</a>
					<a @click.prevent="closeGroupModal" class="btn btn-md btn-primary modal-close" href="#" v-if="!deleteConfirm" v-show="selectedGroup.id !== null"><i class="fas fa-times"></i> Cancel</a>
					<a @click.prevent="saveGroup" class="btn btn-md btn-success" href="#" v-if="!deleteConfirm" v-show="selectedGroup.id !== null"><i class="fas fa-save"></i> Save Changes</a>

					<span class="mr-auto" v-if="deleteConfirm" v-show="selectedGroup.id !== null">Are you sure you want to delete this Property Group?</span>
					<a @click.prevent="deleteGroup" class="btn btn-md btn-danger" href="#" v-if="deleteConfirm" v-show="selectedGroup.id !== null"><i class="fas fa-trash"></i> Delete</a>
					<a @click.prevent="hideDeleteConfirm" class="btn btn-md btn-primary" href="#" v-if="deleteConfirm" v-show="selectedGroup.id !== null"><i class="fas fa-times"></i> Cancel</a>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>
	import draggable from 'vuedraggable';
	import {eventBus} from '../../../app';
	import Errors from '../../../Utils/Errors.js';

	export default {
		components: {
			draggable
		},
		props: {
			dataProviderTypeId: {
				type: Number
			}
		},
		data() {
			return {
				providerTypeId: this.dataProviderTypeId,
				tabName: 'providerTypeGroups',
				loaded: false,
				providerType: {},

				clickedLang: this.$defaultLanguage.locale,
				defaultLocale: this.$defaultLanguage.locale,

				groupModal: false,
				deleteConfirm: false,

				selectedGroup: {
					id: null,
					internal_description: null,
					hidden: null
				},
				selectedGroupIndex: null,

				selectedSite: this.$selectedSite,

				userRoles: this.$currentUserRoles,
				errors: new Errors(),
			}
		},
		mounted() {
			this.fetchDataFromApi();
			let vm = this;

			// When setActiveStatus method ran, change selected data in form fields
			/* From LangSelector */
			eventBus.$on('setActiveStatus', function (clickedLang) {
				vm.clickedLang = clickedLang;
			});
		},
		methods: {
			fetchDataFromApi: function () {
				let vm = this;
				eventBus.$emit('onLoaderStart');
				axios.post('/api/' + vm.$selectedSite.slug + '/providertype/fetchtabdata/' + vm.providerTypeId, {
					tab: vm.tabName
				})
						.then(function (response) {
							if (response.data.hasOwnProperty('data')) {
								if (response.data.data.hasOwnProperty('providerType')) {
									vm.providerType = response.data.data.providerType;
								}
								vm.selectGroup(0);
								eventBus.$emit('onLoaderFinish');
								vm.loaded = true;
							}
						})
						.catch(function (e) {
							eventBus.$emit('onLoaderFinish');
							vm.$toastr.error('Api ' + vm.tabName + ' tab load failed', 'Error');
						});
			},
			showCreateModal: function () {
				let vm = this;

				for (let i = 0; i < vm.$availableLanguages.length; i++) {
					vm.selectedGroup[vm.$availableLanguages[i].locale] = {
						name: '',
						description: '',
					}
				}

				vm.selectedGroup.id = null;
				vm.selectedGroup.internal_description = null;
				vm.selectedGroup.hidden = false;
				this.groupModal = true;
			},
			closeGroupModal: function () {
				if (this.selectedGroupIndex) {
					this.selectGroup(this.selectedGroupIndex);
				}
				this.groupModal = false;
			},
			showEditModal: function (index) {
				this.selectedGroupIndex = index;
				this.selectedGroup = this.providerType.groups[index].translations;
				this.selectedGroup.id = this.providerType.groups[index].id;
				this.selectedGroup.internal_description = this.providerType.groups[index].internal_description;
				this.selectedGroup.name = this.providerType.groups[index].name;

				this.groupModal = true;
			},
			showDeleteConfirm: function () {
				this.deleteConfirm = true;
			},
			hideDeleteConfirm: function () {
				this.deleteConfirm = false;
			},
			generateSelectedGroupData: function () {
				let vm = this;

				for (let i = 0; i < vm.$availableLanguages.length; i++) {
					if (vm.selectedGroup[vm.$availableLanguages[i].locale] && (vm.selectedGroup[vm.$availableLanguages[i].locale].name || vm.selectedGroup[vm.$availableLanguages[i].locale].description)) {
						vm.selectedGroup[vm.$availableLanguages[i].locale] = {
							name: vm.selectedGroup[vm.$availableLanguages[i].locale].name,
							description: vm.selectedGroup[vm.$availableLanguages[i].locale].description,
						}
					} else {
						if (vm.$availableLanguages[i].locale !== vm.$defaultLanguage.locale) {
							vm.selectedGroup[vm.$availableLanguages[i].locale] = null;
						}
					}
				}
			},
			selectGroup: function (id) {
				let vm = this;
				if (vm.providerType.groups.length > 0) {
					vm.selectedGroupIndex = id;
					vm.selectedGroup = _.clone(vm.providerType.groups[id]);

					for (let i = 0; i < vm.$availableLanguages.length; i++) {
						if (vm.selectedGroup.translations[vm.$availableLanguages[i].locale]) {
							vm.selectedGroup[vm.$availableLanguages[i].locale] = {
								name: vm.selectedGroup.translations[vm.$availableLanguages[i].locale].name,
								description: vm.selectedGroup.translations[vm.$availableLanguages[i].locale].description,
							}
						} else {
							vm.selectedGroup[vm.$availableLanguages[i].locale] = {
								name: null,
								description: null
							};
						}
					}
				}
			},
			createGroup: function () {
				let vm = this;

				eventBus.$emit('onLoaderStart');
				vm.generateSelectedGroupData();

				axios.post('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.providerType.id + '/groups', vm.selectedGroup)
						.then(function (response) {
							vm.$toastr.success('Group added successfully.');
							vm.fetchDataFromApi();
							vm.closeGroupModal();
							eventBus.$emit('onLoaderFinish');
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Save new group failed.', 'Error');
							eventBus.$emit('onLoaderFinish');
						});
			},
			saveGroup: function () {
				let vm = this;

				eventBus.$emit('onLoaderStart');
				vm.generateSelectedGroupData();

				vm.selectedGroup.internal_description = vm.selectedGroup['en'].internal_description;
				vm.selectedGroup.name = vm.selectedGroup['en'].name;

				axios.put('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.providerType.id + '/groups/' + vm.selectedGroup.id, vm.selectedGroup)
						.then(function (response) {
							vm.$toastr.success('Group updated successfully.');
							vm.fetchDataFromApi();
							vm.closeGroupModal();
							eventBus.$emit('onLoaderFinish');
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Group update failed.', 'Error');
							eventBus.$emit('onLoaderFinish');
						});
			},
			arrangeGroupOrder: function () {
				let vm = this;
				eventBus.$emit('onLoaderStart');
				axios.post('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.providerType.id + '/arrangeGroupOrder', vm.providerType.groups)
						.then(function (response) {
							vm.$toastr.success('Group order modified successfully.');
							eventBus.$emit('onLoaderFinish');
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Group order modify failed.', 'Error');
							eventBus.$emit('onLoaderFinish');
						});
			},
			deleteGroup: function () {
				let vm = this;

				eventBus.$emit('onLoaderStart');
				axios.delete('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.providerType.id + '/groups/' + vm.selectedGroup.id)
						.then(function (response) {
							vm.$toastr.success('Group deleted successfully.');
							vm.fetchDataFromApi();
							vm.closeGroupModal();
							eventBus.$emit('onLoaderFinish');
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Group delete failed.', 'Error');
							eventBus.$emit('onLoaderFinish');
						});
			},
			arrangePropertyOrder: function (index) {
				let vm = this;

				eventBus.$emit('onLoaderStart');
				vm.selectedGroupIndex = index;
				vm.selectedGroup = vm.providerType.groups[index].translations;
				vm.selectedGroup.id = vm.providerType.groups[index].id;
				vm.selectedGroup.internal_description = vm.providerType.groups[index].internal_description;
				vm.selectedGroup.name = vm.providerType.groups[index].name;
				vm.selectedGroup.properties = vm.providerType.groups[index].properties;

				axios.post('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.providerType.id + '/groups/' + vm.selectedGroup.id + '/arrangePropertyOrder', vm.selectedGroup.properties)
						.then(function (response) {
							vm.$toastr.success('Property order modified successfully.');
							eventBus.$emit('onLoaderFinish');
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Property order modify failed.', 'Error');
							eventBus.$emit('onLoaderFinish');
						});
			},
			deleteProperty: function (id) {
				let vm = this;

				eventBus.$emit('onLoaderStart');
				axios.delete('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.providerType.id + '/groups/' + vm.selectedGroup.id + '/properties/' + id)
						.then(function (response) {
							vm.$toastr.success('Property deleted successfully.');
							eventBus.$emit('onLoaderFinish');
							window.location.reload();
						})
						.catch(function (e) {
							vm.errors.record(e.response.data);
							vm.$toastr.error('Property delete failed.', 'Error');
							eventBus.$emit('onLoaderFinish');
						});
			}
		},
		computed: {
			errorNameCurrentLanguage: function () {
				return this.clickedLang + '_name';
			},
			errorDescriptionCurrentLanguage: function () {
				return this.clickedLang + '_description';
			},
		}
	}
</script>

<style scoped>

</style>
